/* The app shell: header + rail + workspace outlet, and the mediation between them.

   Three behaviours worth knowing about:

   1. Workspaces mount lazily and then STAY mounted (hidden, not destroyed).
      That's what makes constitution §11 structural — scroll position, filters
      and (later) the map viewport survive workspace switching for free. It's
      also why the outlet is managed imperatively rather than re-rendered.

   2. Search is mediated, not wired. The header reports a query; the shell hands
      it to the active workspace. Each workspace keeps its OWN query, so
      switching away and back restores it (§11 again).

   3. Rail-collapsed and theme are *preferences* and persist in localStorage.
      Workspace state is not persisted — §12, refresh starts fresh.

   4. Detail is layered, not routed to. It mounts OVER the outlet while the
      workspace underneath stays exactly as it was, which is what makes §10
      ("Detail returns you to where you came from, restored") free rather than
      a state-restoration problem. The shell owns the playlist so the URL and
      the position can never disagree, and it delivers `Intent`s between parts
      of the app that cannot see each other. */

import { LitElement, html, css, nothing } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, state } from 'lit/decorators.js';
import { Router, type Route } from '../../core/router.js';
import { getMe, getMeta, inFlightCount, NOT_SIGNED_IN } from '../../core/api.js';
import { submitPendingShare } from '../../core/share.js';
import { reconcileIdentity } from '../../core/cache.js';
import { attachShellGestures, type Sample } from '../../core/gestures.js';
import type {
  Intent, IntentTarget, Me, PlaylistSource, Refreshable, ThemePref, WorkspaceDef,
} from '../../core/types.js';
import * as theme from '../../core/theme.js';
import { SEARCH_TAB, TABS, WORKSPACES, byId } from '../routes.js';
import '../g-header/g-header.js';
import '../g-rail/g-rail.js';
import '../g-signin/g-signin.js';
import '../g-onboarding/g-onboarding.js';
import '../g-tabbar/g-tabbar.js';
import '../../component/g-button/g-button.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';
import '../../workspace/g-detail/g-detail.js';

const RAIL_KEY = 'grain-rail-collapsed';

/** The `open-detail` payload every call site dispatches. */
interface OpenDetail {
  ids: number[];
  at: number;
  source?: PlaylistSource;
}

@customElement('g-shell')
export class GShell extends LitElement {
  static override styles = [shadowReset, css`
    /* 100% of <body>, NOT 100dvh — the difference is load-bearing in the
       installed PWA.

       Measured on the reference device in standalone: 100dvh computes to 775px
       while innerHeight (and documentElement.clientHeight) is 719px. Chrome's
       dynamic viewport there excludes the status bar but not the system
       navigation bar, so a dvh-sized shell overhangs the visible area by exactly
       the nav bar's height and the tab bar — the last thing in the column —
       renders underneath it. The bottom safe-area inset does not rescue this: it
       reports 0px on that device, because the nav bar is not treated as a
       display cutout.

       html and body are both height:100% (design/base.ts), so 100% resolves to
       clientHeight, which tracks the genuinely visible box. It is also the safer
       choice in an iOS browser tab, where dvh grows and shrinks as the toolbars
       hide and the shell would reflow with it. */
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    .body {
      position: relative;
      display: flex;
      flex: 1;
      min-height: 0;
    }
    /* The outlet frames the workspaces; each workspace scrolls ITSELF.
       (ADR 0010.)

       This was one shared scroller until a real-device test showed what that
       costs: hiding the scrolled child momentarily collapses scrollHeight, the
       browser clamps scrollTop to 0, and the position is gone. Measured —
       Library at 2630, switch to Index, switch back, 0. So ADR 0002 §11's claim
       that scroll survives a workspace switch "for free" because workspaces stay
       mounted was never true: the DOM, filters and query survived, but the
       scroll lived out here on the outlet, not on the workspace.

       Giving each workspace its own scrollport makes the claim true, because a
       scroll offset on an element IS preserved across display:none — measured
       the same way: 1500, hide, show, 1500.

       It is also what makes a horizontal workspace transition possible at all.
       Translating two workspaces past each other means positioning them, and
       positioning them inside a shared scroller collapses that scroller
       mid-gesture. */
    #outlet {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }
    #outlet > * {
      height: 100%;
      overflow: auto;
      /* Stops scroll chaining to the document, and is the precondition for a
         custom pull-to-refresh: it also suppresses iOS's rubber-band, so the
         overscroll reads the same on both platforms. */
      overscroll-behavior-y: contain;
    }

    /* The one layout swap (ADR 0007 principle 3): below the breakpoint the
       header and rail go, and the tab bar arrives. Both are in the DOM at all
       times and CSS picks — "display: none" also removes the hidden one from the
       accessibility tree, so there is never a second nav to tab through.

       Width, not a pointer:coarse query, because this is a layout question and
       principle 2 splits them: capability decides affordances, width decides
       arrangement. A narrow desktop window genuinely does want the tab bar.
       BP_DESKTOP (960) — media queries can't read a custom property, so the
       value is mirrored from design/tokens.ts. */
    g-tabbar {
      display: none;
    }
    @media (max-width: 959px) {
      g-header,
      g-rail {
        display: none;
      }
      g-tabbar {
        display: flex;
      }
      /* No header means the workspace starts at the top of the screen, and
         index.html asks iOS for a translucent status bar — so the outlet owns
         the top inset that the header used to absorb. */
      #outlet {
        padding-top: var(--safe-t);
      }
    }
    /* hidden workspaces stay in the DOM to preserve their state */
    #outlet > [hidden] {
      display: none;
    }
    /* A failed share is the one thing the shell itself has to report: it happens
       before any workspace is involved, and the share sheet has already closed,
       so there is nowhere else for it to land. Sits under the header rather than
       floating, so it can't cover anything. */
    .share-error {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      flex: none;
      padding: var(--space-2) var(--space-4);
      border-bottom: 1px solid var(--border);
      background: var(--surface);
    }
    .share-error g-notice {
      flex: 1;
    }
    /* A WorkspaceDef.fill workspace (Map) is the viewport rather than a document
       in it: exactly the outlet's height, scrolling nothing but its own panes.
       The height resolves because #outlet is a stretched flex item and therefore
       has a definite height — it is not height:auto, which would collapse this
       to nothing. The overflow rule is belt-and-braces: without it a 1px
       rounding difference gives the outlet a scrollbar over a map that is
       already exactly the right size. */
    #outlet > [data-fill] {
      height: 100%;
      overflow: hidden;
    }
    /* ...and on mobile it also escapes the top safe-area inset the outlet adds
       for every other workspace. A fill workspace IS the viewport rather than
       a document in it, and a viewport that stops short of the status bar is not
       one — Map bleeds underneath and paints its own scrim there (ADR 0012
       decision 10). The negative margin has to be paid back in height or the
       workspace ends short of the tab bar by the same amount.

       Keyed off the fill flag rather than a new one because it has exactly one subject;
       if a second fill workspace ever wants the inset back, split them then.
       This rule sits AFTER the block above deliberately — same specificity, so
       source order is what lets the height override land.

       BP_DESKTOP (960) - 1, mirrored from design/tokens.ts. */
    @media (max-width: 959px) {
      #outlet > [data-fill] {
        margin-top: calc(var(--safe-t) * -1);
        height: calc(100% + var(--safe-t));
      }
    }

    /* Pull-to-refresh indicator. Absolutely placed over the workspace rather
       than inserted above it: a element that takes height would push the content
       down as it appears, which is the jump the gesture is supposed to feel like
       it is *revealing*, not causing.

       The travel is expressed in CSS off a unitless progress (--p, which is 1 at
       the trigger point) rather than the px offset the drag produces, because the
       two ends of that travel are things only CSS knows: -40px puts the 32px disc
       fully off the top, and the resting end has to clear --safe-t. On a notched
       iPhone that inset is ~47-59px, so a fixed offset parked the spinner
       underneath the notch; on Android --safe-t reports 0px and the shell already
       starts below the status bar, which is why the 16px is additive rather than
       a max(). */
    .pull {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      display: grid;
      place-items: center;
      /* Never eats a tap — the gesture is over the workspace, not over this. */
      pointer-events: none;
      z-index: 4;
      color: var(--accent);
      transform: translateY(
        calc(-40px + var(--p, 0) * (var(--safe-t, 0px) + 56px))
      );
      /* Interpolates the two moments the finger is NOT setting the value: the
         snap back to the trigger point on release, and the retract once the
         refresh settles. Without it both were instant, and the retract also
         unmounted the element in the same frame — the indicator appeared to jump
         back up rather than withdraw. Same shape as g-sheet's own transition. */
      transition:
        transform 300ms cubic-bezier(0.32, 0.72, 0, 1),
        opacity 200ms linear;
    }
    /* Nothing to interpolate while a finger is setting the value directly. */
    .pull.drag {
      transition: none;
    }
    .pull .disc {
      display: grid;
      place-items: center;
      width: 32px;
      height: 32px;
      border-radius: var(--radius-full);
      background: var(--surface);
      box-shadow: var(--shadow-1);
      --icon-size: 18px;
    }
    .pull.spin .disc {
      animation: pullspin 700ms linear infinite;
    }
    @keyframes pullspin {
      to {
        rotate: 360deg;
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .pull {
        transition: none;
      }
      .pull.spin .disc {
        animation: none;
      }
    }
  `];

  @state() private active = '';
  @state() private collapsed = localStorage.getItem(RAIL_KEY) === '1';
  /** Mirrors core/theme, which is the owner — see the subscription below. */
  @state() private themePref: ThemePref = theme.getPref();
  @state() private me: Me | null = null;
  /**
   * Three states, not a boolean, because "we haven't asked yet" is genuinely
   * different from both answers.
   *
   * `loading` renders the chrome but mounts no workspace. A two-state version
   * defaulted to signed-in and mounted Library before /api/me resolved: it
   * fetched, took a 401, and was then destroyed when the 401 switched the
   * template to the sign-in card — leaving a detached element in `elements` that
   * `syncOutlet` considered already mounted, so after signing in the outlet
   * stayed empty until a reload.
   */
  @state() private session: 'loading' | 'in' | 'out' = 'loading';
  @state() private queueCount = 0;
  /**
   * Why a shared reel wasn't saved. Only failures surface — usually "that isn't
   * a reel URL", which is what sharing an ordinary post produces and the one
   * thing the user can act on.
   */
  @state() private shareError = '';
  /** The open playlist, as item ids. Null when Detail is closed. */
  @state() private playlist: number[] | null = null;
  @state() private at = 0;
  /** What the open playlist has in common. Null when there is no thread. */
  @state() private source: PlaylistSource | null = null;

  /** Per-workspace query, so §11 survives a switch. */
  private queries = new Map<string, string>();
  /** Mounted workspace elements, keyed by id. Never removed once created. */
  private elements = new Map<string, HTMLElement>();

  /**
   * Tab semantics on touch, a browsing stack on a pointer device — decided here,
   * once, and never re-derived (ADR 0007 decision 7). A resize must not change
   * it: entries pushed under one model would be popped under the other.
   */
  private router = new Router();
  private unwatchTheme?: () => void;
  private unwatchPref?: () => void;
  private detachGestures?: () => void;

  /** How far the pull-to-refresh gesture has been dragged, in px after damping. */
  @state() private pull = 0;
  /** True from the moment a pull commits until the workspace's refresh settles. */
  @state() private refreshing = false;
  /** True while a finger owns `pull`, which is when the transition must stand down. */
  @state() private dragging = false;
  /**
   * Keeps the indicator mounted past `pull === 0` so the retract can animate.
   * Rendering purely off `pull > 0` unmounted it in the same frame the value
   * changed, so there was nothing left to transition.
   */
  @state() private settling = false;
  /** Invalidates a settle timer that a new pull has overtaken. */
  private settleId = 0;

  override connectedCallback(): void {
    super.connectedCallback();
    this.router.start(
      WORKSPACES.map((w) => w.id),
      (route) => this.onRoute(route),
    );
    this.unwatchTheme = theme.watchSystem(() => this.requestUpdate());
    // Settings can change the preference too, so re-read rather than assuming
    // the header's button is the only way it moves.
    this.unwatchPref = theme.subscribe(() => {
      this.themePref = theme.getPref();
    });
    void this.loadSession();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.router.stop();
    this.unwatchTheme?.();
    this.unwatchPref?.();
    this.detachGestures?.();
  }

  /**
   * Attach the touch gestures to `.body`, not to the host.
   *
   * `.body` carries `inert` while Detail is open, which suppresses its pointer
   * events — so Detail's exclusion from both gestures (ADR 0010) falls out of
   * the existing overlay handling rather than needing a check of its own. It
   * also keeps the tab bar out of the gesture surface, where a horizontal drag
   * would otherwise switch workspaces while the thumb is aiming at a tab.
   */
  private attachGestures(): void {
    if (this.detachGestures) return;
    const body = this.renderRoot.querySelector('.body');
    if (!(body instanceof HTMLElement)) return;
    this.detachGestures = attachShellGestures(body, {
      scrollTop: () => this.activeElement()?.scrollTop ?? 0,
      onPullStart: () => this.startPull(),
      onPullMove: (s) => this.movePull(s),
      onPullEnd: (s) => void this.endPull(s),
    });
  }

  /** The mounted element for the active workspace, if there is one. */
  private activeElement(): HTMLElement | undefined {
    return this.elements.get(this.active);
  }

  /**
   * Navigate — or, when the tap lands on the workspace already showing, scroll
   * that workspace back to the top.
   *
   * The convention on both platforms, and it earns its place here rather than
   * being copied: Library is 654 tiles deep and pull-to-refresh only arms at
   * `scrollTop 0`, so without this the only way back to the top of a long list
   * is to fling repeatedly — and refresh is unreachable until you get there.
   * It is also the non-gesture path to the top, which matters because a
   * gesture-only affordance is no affordance at all for anyone using assistive
   * tech (ADR 0010 decision 2).
   *
   * Shared by the rail and the tab bar. Both emit the same `navigate` event and
   * neither should have to know the difference.
   */
  private onNavigate(id: string): void {
    // With Detail open, `go` is what closes it — the tab tapped may well be the
    // active one, and scrolling the workspace underneath instead would leave the
    // overlay up. Unreachable today (both navs are inert while Detail is open),
    // but the ordering here is the part that would be wrong if that changed.
    if (id === this.active && !this.playlist) {
      this.scrollActiveToTop();
      return;
    }
    this.router.go(id);
  }

  /**
   * A `fill` workspace scrolls its own panes rather than itself, so this is a
   * no-op on Map. Deliberately not special-cased into an intent: "scroll the
   * thing that scrolls" is the workspace's business, and Map's list is behind a
   * sheet that does not exist yet.
   */
  private scrollActiveToTop(): void {
    const el = this.activeElement();
    if (!el || el.scrollTop === 0) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }

  /* ---- pull to refresh -------------------------------------------------- */

  /** Px of pull that commits a refresh. */
  private static readonly PULL_TRIGGER = 64;
  /** Floor on how long the spinner stays visible, so a fast refresh doesn't
      flash it for one frame and retract — see the finally block below. */
  private static readonly SPIN_MIN_MS = 1000;
  /** Must cover the longest transition on `.pull`, or the retract gets cut off. */
  private static readonly SETTLE_MS = 320;

  private startPull(): boolean {
    if (this.refreshing) return false;
    const def = byId(this.active);
    if (!def?.refreshable || !this.activeElement()) return false;
    // Take the indicator off whatever settle it was in the middle of: the finger
    // owns the value again, and a pending timer must not retract underneath it.
    this.settleId++;
    this.settling = false;
    this.dragging = true;
    return true;
  }

  /**
   * Damped so the indicator never tracks the finger 1:1.
   *
   * A linear pull invites dragging the sheet halfway down the screen; the square
   * root makes the first 64px cheap and everything after it expensive, which is
   * what communicates "this is as far as it goes".
   */
  private movePull(s: Sample): void {
    this.pull = Math.min(96, Math.sqrt(Math.max(0, s.dy)) * 7);
  }

  private async endPull(_s: Sample): Promise<void> {
    const committed = this.pull >= GShell.PULL_TRIGGER;
    // The finger is gone, so the transition takes over from here — both for the
    // abandoned case below and for the snap back to the trigger point.
    this.dragging = false;
    if (!committed) {
      this.pull = 0;
      await this.finishSettle();
      return;
    }
    // Hold the indicator at the trigger point while the work happens, so it
    // reads as "working" rather than snapping back and leaving no feedback.
    this.pull = GShell.PULL_TRIGGER;
    this.refreshing = true;
    const el = this.activeElement() as (HTMLElement & Partial<Refreshable>) | undefined;
    const started = performance.now();
    try {
      // Alongside the workspace's own refresh, not gated on it: queueCount is
      // shell state (the avatar/settings tab badge), not the workspace's, so it
      // belongs here rather than in each refreshable workspace's own refresh().
      await Promise.all([el?.refresh?.(), this.loadQueueCount()]);
    } catch {
      /* A failed refresh is the workspace's to report — it owns the error line
         it already renders for a failed load. The indicator must still retract,
         or the app looks stuck. */
    } finally {
      // A refresh that resolves in a few ms otherwise flashes the spinner for
      // one frame and yanks it away, which reads as a jerk rather than a
      // completed action — hold it up to the floor before retracting.
      const remaining = GShell.SPIN_MIN_MS - (performance.now() - started);
      if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
      this.refreshing = false;
      this.pull = 0;
      await this.finishSettle();
    }
  }

  /**
   * Hold the indicator in the DOM at `pull === 0` long enough for the retract to
   * play, then drop it.
   *
   * Guarded by a generation counter so a pull started mid-retract wins: its
   * `startPull` bumps the id, and this stale continuation then leaves `settling`
   * alone rather than tearing down the new gesture's indicator.
   */
  private async finishSettle(): Promise<void> {
    const id = ++this.settleId;
    this.settling = true;
    await new Promise((r) => setTimeout(r, GShell.SETTLE_MS));
    if (this.settleId === id) this.settling = false;
  }

  private async loadSession(): Promise<void> {
    const me = await getMe();
    if (me === NOT_SIGNED_IN) {
      this.session = 'out';
      return;
    }
    this.onSignedIn(me);
  }

  /**
   * Boot with an identity, from either path: a cookie we already had, or a fresh
   * sign-in. `g-signin` reports the `Me` that /api/auth/session returned, so the
   * two paths differ only in where the payload came from.
   */
  private onSignedIn(me: Me): void {
    this.me = me;
    this.session = 'in';
    // The one funnel into a session, which is what makes it the right place to
    // notice the identity changed since the last one and drop that user's
    // cached thumbnails. Fire-and-forget: nothing on screen waits on it.
    void reconcileIdentity(me.email);
    void this.loadQueueCount();
    // A share that landed while signed out waits here rather than failing: this
    // runs on both paths into a session, so signing in to save one works.
    void this.flushShare();
  }

  /**
   * Leave the onboarding wizard.
   *
   * Re-reads /api/me rather than flipping `me.onboarded` locally, because the
   * server is the only thing that knows the taxonomy actually applied — and
   * because the template swap below is what finally lets `syncOutlet` mount a
   * workspace. Mutating the field in place would work by accident today and
   * break the moment /complete gains a partial-failure case.
   */
  private async onOnboarded(): Promise<void> {
    const me = await getMe();
    if (me === NOT_SIGNED_IN) {
      this.session = 'out';
      return;
    }
    this.me = me;
  }

  /**
   * Submit a share captured at boot, and say what happened.
   *
   * The success case refreshes the queue count instead of announcing itself —
   * the badge ticking up is the confirmation, and it is the same signal a
   * webhook save gives (§23: no notice for a thing the UI already shows).
   */
  private async flushShare(): Promise<void> {
    const outcome = await submitPendingShare();
    if (outcome === null) return;
    if (outcome.ok) {
      void this.loadQueueCount();
      return;
    }
    this.shareError = outcome.reason;
  }

  private async loadQueueCount(): Promise<void> {
    // Fetched once this pass; the live-polling indicator arrives with the
    // Library pass, which already needs /api/meta for its category chips.
    try {
      const meta = await getMeta();
      if (meta !== NOT_SIGNED_IN) this.queueCount = inFlightCount(meta);
    } catch {
      /* the indicator is decorative — never let it break the shell */
    }
  }

  /**
   * The router is the authority on whether Detail is open.
   *
   * A route carrying an item id that we have no playlist for is a cold deep
   * link (or a Forward back into one) — it opens as a playlist of one, which is
   * §12: refresh starts fresh, including the list you were paging through.
   */
  private onRoute(route: Route): void {
    this.active = route.workspace;
    if (route.itemId === null) {
      this.playlist = null;
      this.source = null;
      return;
    }
    if (!this.playlist?.includes(route.itemId)) {
      this.playlist = [route.itemId];
      this.at = 0;
      // A cold deep link has no origin, so it has no thread either.
      this.source = null;
    } else {
      this.at = this.playlist.indexOf(route.itemId);
    }
  }

  private openDetail(e: CustomEvent<OpenDetail>): void {
    const { ids, at, source } = e.detail;
    if (!ids.length) return;
    this.playlist = ids;
    this.at = Math.min(Math.max(0, at), ids.length - 1);
    this.source = source ?? null;
    this.router.openItem(ids[this.at]);
  }

  /** Move within the open playlist. Replaces the history entry — see Router. */
  private navigateDetail(at: number): void {
    const ids = this.playlist;
    if (!ids || at < 0 || at >= ids.length) return;
    this.at = at;
    this.router.openItem(ids[at]);
  }

  /**
   * Deliver an intent to every mounted workspace that accepts one.
   *
   * Broadcast rather than addressed: `item-deleted` genuinely concerns all of
   * them (Library holds the tile, Map holds the venue's item list, Index holds
   * the mention's ids), and a workspace that doesn't care simply doesn't
   * implement `applyIntent`.
   */
  private broadcast(intent: Intent): void {
    for (const el of this.elements.values()) {
      (el as Partial<IntentTarget>).applyIntent?.(intent);
    }
  }

  /**
   * Deliver an intent to the ACTIVE workspace only.
   *
   * The counterpart to `broadcast`, and the distinction is the point: broadcasting
   * `open-search` would open the field on every mounted workspace, so leaving Map
   * would reveal a stray search field waiting on Library.
   */
  private send(intent: Intent): void {
    const el = this.activeElement();
    if (el) (el as Partial<IntentTarget>).applyIntent?.(intent);
  }

  /**
   * A place chip in Detail: close, go to Map, select the venue there.
   *
   * The header query is cleared as part of the move. Map's own filters could
   * otherwise exclude the very venue we are asking it to select, and landing on
   * a map with an invisible selection is worse than not offering the jump.
   */
  private showPlace(mapPlaceId: number): void {
    this.queries.set('map', '');
    // One history operation: `go` replaces the item entry, which both closes
    // Detail and lands on Map. Closing first and navigating second raced — see
    // the note on Router.go.
    this.router.go('map');
    void this.updateComplete.then(() => {
      this.broadcast({ kind: 'show-place', mapPlaceId });
    });
  }

  private onItemDeleted(id: number): void {
    this.broadcast({ kind: 'item-deleted', id });
    const rest = (this.playlist ?? []).filter((x) => x !== id);
    if (!rest.length) {
      this.router.closeItem();
      return;
    }
    this.playlist = rest;
    this.at = Math.min(this.at, rest.length - 1);
    this.router.openItem(rest[this.at]);
  }

  private onQuery(e: CustomEvent<string>): void {
    this.queries.set(this.active, e.detail);
    this.syncOutlet();
    this.requestUpdate();
  }

  private toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    localStorage.setItem(RAIL_KEY, this.collapsed ? '1' : '0');
  }

  /**
   * Create a workspace element if it doesn't exist yet, and make sure it is in
   * the outlet. Idempotent.
   *
   * Attaches by parent check rather than only on creation. `elements` outlives
   * the outlet — switching between the sign-in and shell templates makes Lit
   * rebuild this subtree — and an element remembered but detached would leave
   * the workspace permanently blank.
   */
  private mount(def: WorkspaceDef, outlet: Element): HTMLElement {
    let el = this.elements.get(def.id);
    if (!el) {
      el = document.createElement(def.tag);
      if (def.fill) el.setAttribute('data-fill', '');
      this.elements.set(def.id, el);
    }
    if (el.parentNode !== outlet) outlet.append(el);
    return el;
  }

  /** Create the active workspace on first visit; hide the others. */
  private syncOutlet(): void {
    // Nothing mounts without an identity: every workspace scopes its reads to
    // the caller, so mounting one before /api/me answers buys a guaranteed 401.
    if (this.session !== 'in') return;
    const outlet = this.renderRoot.querySelector('#outlet');
    const def = byId(this.active);
    if (!outlet || !def) return;

    const el = this.mount(def, outlet);
    for (const [id, node] of this.elements) {
      node.toggleAttribute('hidden', id !== def.id);
    }
    (el as HTMLElement & { query?: string }).query = this.queries.get(def.id) ?? '';
  }

  override updated(): void {
    this.syncOutlet();
    // Deferred to here rather than firstUpdated: `.body` only exists once the
    // session resolves to signed-in, and firstUpdated runs against the loading
    // template. attachGestures is idempotent.
    this.attachGestures();
  }

  override render() {
    // Signed out replaces the shell rather than covering it: there is nothing
    // underneath to preserve, and an overlay would leave a tabbable rail behind
    // the card. `loading` keeps the chrome — only the outlet waits.
    if (this.session === 'out') {
      return html`<g-signin
        @signed-in=${(e: CustomEvent<Me>) => this.onSignedIn(e.detail)}
      ></g-signin>`;
    }

    // Signed in but never onboarded: same substitution, same reasoning as
    // sign-out. It is deliberately NOT a route or a workspace — the taxonomy has
    // to be settled before the first reel is analysed, and nothing behind this is
    // worth reaching yet (ADR 0014). Every pre-wizard account is grandfathered
    // onboarded by migration 013, so this can only fire for a genuinely new user.
    if (this.me && !this.me.onboarded) {
      return html`<g-onboarding @done=${() => void this.onOnboarded()}></g-onboarding>`;
    }

    const def = byId(this.active);
    const open = this.playlist !== null;
    return html`
      <g-header
        ?inert=${open}
        .searchable=${def?.searchable ?? false}
        .placeholder=${def?.searchPlaceholder ?? ''}
        .query=${this.queries.get(this.active) ?? ''}
        .queueCount=${this.queueCount}
        .me=${this.me}
        .theme=${this.themePref}
        @g-input=${this.onQuery}
        @toggle-theme=${() => theme.cycle()}
      ></g-header>

      ${this.shareError
        ? html`<div class="share-error" ?inert=${open}>
            <g-notice tone="danger">${this.shareError}</g-notice>
            <g-button
              icon-only
              label="Dismiss"
              @click=${() => {
                this.shareError = '';
              }}
            ><g-icon name="x"></g-icon></g-button>
          </div>`
        : nothing}

      <!-- inert while Detail is open: the workspace stays mounted and rendered
           (that is the whole point), but it must not be tabbable or clickable
           through the overlay. -->
      <div class="body" ?inert=${open}>
        <g-rail
          .items=${WORKSPACES}
          .active=${this.active}
          .collapsed=${this.collapsed}
          @navigate=${(e: CustomEvent<string>) => this.onNavigate(e.detail)}
          @toggle-collapse=${this.toggleCollapse}
        ></g-rail>
        <!-- The same g-input the header sends. Below the breakpoint the header is
             gone and the query comes from the active workspace's own filter band
             instead, but it lands in exactly one place either way: search is
             mediated, not wired (see 2 above). -->
        <main
          id="outlet"
          @open-detail=${(e: CustomEvent<OpenDetail>) => this.openDetail(e)}
          @g-input=${this.onQuery}
        ></main>

        <!-- Rendered only while it is doing something: at rest there is no
             element, so nothing overlays the workspace and nothing to
             mis-report. The settling flag extends "doing something" through the
             retract, which is what gives the transition a subject.

             Inline because it changes at touch frequency — but only the
             progress, not the geometry: where 0 and 1 land is CSS's business
             (see the note on the .pull rule). -->
        ${this.pull > 0 || this.settling
          ? html`<div
              class="pull ${this.dragging ? 'drag' : ''} ${this.refreshing ? 'spin' : ''}"
              style="--p: ${this.pull / GShell.PULL_TRIGGER}; opacity: ${Math.min(
                1,
                this.pull / 40,
              )}"
            >
              <span class="disc"><g-icon name="gear"></g-icon></span>
            </div>`
          : nothing}
      </div>

      <!-- The rail's mobile counterpart, hidden by CSS above the breakpoint.
           Detail is position:fixed and covers it, so it needs no inert flag of
           its own — unlike .body, which stays in flow underneath. -->
      <!-- TABS, not WORKSPACES: the bar carries the search action as well, and in
           its own order. The rail keeps the plain workspace list. Search is
           unusable wherever the workspace declares no search, which derives the
           Settings case instead of naming it. -->
      <g-tabbar
        .items=${TABS}
        .active=${this.active}
        .badges=${{ settings: this.queueCount }}
        .disabledActions=${def?.searchable ? [] : [SEARCH_TAB.id]}
        .me=${this.me}
        ?inert=${open}
        @navigate=${(e: CustomEvent<string>) => this.onNavigate(e.detail)}
        @search=${() => this.send({ kind: 'open-search' })}
      ></g-tabbar>

      ${open
        ? html`<g-detail
            .ids=${this.playlist ?? []}
            .at=${this.at}
            .origin=${def?.label ?? ''}
            .thread=${this.source?.label ?? ''}
            .icon=${byId(this.source?.workspace ?? this.active)?.icon ?? ''}
            @close=${() => this.router.closeItem()}
            @navigate=${(e: CustomEvent<number>) => this.navigateDetail(e.detail)}
            @show-place=${(e: CustomEvent<number>) => this.showPlace(e.detail)}
            @item-deleted=${(e: CustomEvent<number>) => this.onItemDeleted(e.detail)}
          ></g-detail>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-shell': GShell;
  }
}
